import { displayedMRN } from "@tailormed/common-client/util/mrn";
import { Button, Space, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Link } from "react-router-dom";

import OrderRequiredIcon from "../../assets/svg/OrderRequiredIcon";
import { ShorterTextWithTooltip } from "../ShorterTextWithTooltip";
import { capitalizeFirstLetter } from "../../utils/string";
import { ActionButton } from "../ActionButton";
import { Statuses } from "../../constant/status";
import {
  BLUE,
  BLUE_500,
  BLUE_675,
  BLUE_700,
  GRAY_500,
  LIGHT_BLUE_100,
  LIGHT_BLUE_50,
  WHITE
} from "../../constant/colors";
import { fontWeights, sizes } from "../../constant/styles";

const { Text } = Typography;

const StyledDiv = styled.div`
  ::-webkit-scrollbar {
    -webkit-appearance: none;
  }

  ::-webkit-scrollbar:vertical {
    width: 15px;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 8px;
    border: 4px solid ${WHITE};
    background-color: rgba(0, 0, 0, 0.2);
  }
`;

const ResponsiveDroppableContainer = styled(StyledDiv)`
  background: ${(props) => (props.isDraggingOver ? LIGHT_BLUE_50 : LIGHT_BLUE_100)};
  padding: ${(props) => (props.dailyOrdersLength > 2 ? "8px 8px 0px 8px" : "8px")};
  min-height: 192px;
  border: ${(props) => (props.mark ? `2px solid ${BLUE_700}` : "none")};
  max-height: ${(props) => (props.mark ? "416px" : props.dailyOrdersLength > 2 ? "420px" : "auto")};
  overflow-y: ${(props) => (props.dailyOrdersLength > 2 ? "auto" : "hidden")};
  overflow-x: hidden;

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px;

  @media (max-width: 1920px) {
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  }

  @media (max-width: 1830px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  @media (max-width: 1700px) {
    grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  }

  @media (max-width: 1600px) {
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  }

  @media (max-width: 1550px) {
    grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  }

  @media (max-width: 1410px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  }

  @media (max-width: 1366px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }

  @media (max-width: 1280px) {
    grid-template-columns: repeat(auto-fill, minmax(110px, 1fr));
  }

  @media (max-width: 1352px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  }
`;

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  padding: grid,
  margin: `0 0 ${grid}px 0`,
  background: "white",
  border: isDragging ? `1.5px dashed ${BLUE_500}` : `1.5px solid ${BLUE}`,
  borderRadius: "2px",
  boxShadow: isDragging ? `0px 4px 12px ${BLUE_675}` : null,
  height: 190,
  display: "flex",
  justifyContent: "center",
  ...draggableStyle
});

const getDynamicMaxLength = () => {
  const width = window.innerWidth;
  if (width >= 2400) return 28;
  if (width >= 1920) return 19;
  if (width >= 1440) return 10;
  return 8;
};

const Order = ({ order, site, onClickCalendar }) => {
  const { t } = useTranslation();
  const { patient } = order;
  const { first_name, middle_name, last_name, id, mrn } = patient;
  const isCurrentCycle = order.currentOrder.id === order.pharmacyPatientCurrentOrder;
  const [maxLength, setMaxLength] = useState(getDynamicMaxLength());

  const statusObj = Object.values(Statuses).find((s) => s.key === order.currentOrder.status);
  const statusIcon = statusObj?.icon || <OrderRequiredIcon />;

  useEffect(() => {
    const handleResize = () => setMaxLength(getDynamicMaxLength());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <div style={{ width: "100%" }} onClick={() => onClickCalendar(order)}>
        <Space>
          <Button shape="circle" style={{ border: `1px solid ${BLUE}` }} icon={statusIcon} />
          <Space direction="vertical" size={0}>
            <ShorterTextWithTooltip
              text={`${first_name ? `${first_name}${middle_name ? ` ${middle_name}` : ""} ${last_name}` : "Patient"}`}
              maxLength={maxLength}
              strong={true}
              id="cv_patient_name"
            />
            {displayedMRN(mrn) && (
              <ShorterTextWithTooltip text={`MRN-${displayedMRN(mrn)}`} maxLength={maxLength} id="cv_patient_mrn" />
            )}
            <ShorterTextWithTooltip text={`${site}-${id}`} maxLength={maxLength} id="cv_patient_id" />
          </Space>
        </Space>
        {order.drugManufacturer && (
          <Space size={1} direction="vertical">
            <ShorterTextWithTooltip text={order.rxnormDrug?.tmDisplayName || ""} maxLength={maxLength + 6} />
            <ShorterTextWithTooltip
              text={capitalizeFirstLetter(order.potentialSaving?.providedBy)}
              maxLength={maxLength + 6}
            />
          </Space>
        )}
        <Text
          style={{
            color: GRAY_500,
            fontSize: sizes.small,
            fontWeight: fontWeights.regular,
            display: "block",
            lineHeight: "14px",
            marginTop: "5px"
          }}
        >
          {t("form.cycle.cycle_number", { cycleNum: order.currentOrder.orderIndex + 1 }) +
            ` ${isCurrentCycle ? t("form.cycle.current_indicator") : ""}`}
        </Text>
      </div>
      <ActionButton patient={order} isCalenderView={true} actionButtonId="cycle_action_btn_list" />
    </Space>
  );
};

const DroppableDay = ({ site, dayIndex, dailyOrders, mark, onClickCalendar }) => {
  return (
    <Droppable droppableId={dayIndex.toString()}>
      {(provided, snapshot) => (
        <ResponsiveDroppableContainer
          ref={provided.innerRef}
          isDraggingOver={snapshot.isDraggingOver}
          dailyOrdersLength={dailyOrders.length}
          mark={mark}
        >
          {dailyOrders.map((order, index) => (
            <Draggable key={order.currentOrder.id} draggableId={order.currentOrder.id.toString()} index={index}>
              {(provided, snapshot) => (
                <Link
                  id="cv-patient-square"
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                  to={{
                    pathname: window.location.pathname,
                    search: `?patient=${order.patient.id}&pharmacyPatient=${order.id}`
                  }}
                >
                  <Order order={order} site={site} onClickCalendar={onClickCalendar} />
                </Link>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </ResponsiveDroppableContainer>
      )}
    </Droppable>
  );
};

export { DroppableDay };
